import MainCard from 'components/MainCard';
import { Box, Stack } from '@mui/system';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Tooltip,
} from '@mui/material';
import { PopupTransition } from 'components/@extended/Transitions';
import { UploadFileDialog } from './partials/upload-file-dialog';
import BaseTable from 'components/base-table';
import { useFileListColumnsConfig, useFileManagement } from './hooks';
import { GenericFileDownloadDto, GenericTableDataFileDto } from 'inside-fifa/api/generic-tables/dto';
import { useCallback, useState } from 'react';
import { UploadFileFormValidValues } from './partials/upload-file-dialog/dto';
import { uploadFile } from 'inside-fifa/api/generic-tables/upload-file';
import { DefaultVisibleDataFileTableColumnConfig } from './consts';
import { FormattedMessage } from 'react-intl';

const EMPTY_FILE_UPLOAD_FORM = { file: null, fileName: '' };

const GenericTablesDataPage = () => {
    const { files, pageCount, fetchFiles, refreshList, isManagementAllowed, deleteFile, downloadFile } =
        useFileManagement();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedFileToDelete, setSelectedFile] = useState<GenericTableDataFileDto>();
    const [selectedFileToDownload, setSelectedFileDownload] = useState<GenericTableDataFileDto>();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const handleDeleteFile = useCallback(async (file: GenericTableDataFileDto): Promise<GenericTableDataFileDto> => {
        setSelectedFile(file);
        setConfirmDialogOpen(true);
        return file;
    }, []);

    const handleDownload = useCallback(
        async (file: GenericTableDataFileDto): Promise<GenericFileDownloadDto> => {
            setSelectedFileDownload(file);
            try {
                const response = await downloadFile(file);
                if (response.status === 200) {
                    if (response.url) {
                        const a = document.createElement('a');
                        a.href = response.url;
                        a.download = file.name || 'downloaded-file';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(response.url);
                    }
                    setSnackbarMessage(`File downloaded successfully.`);
                    setSnackbarOpen(true);
                }
                return response;
            } catch (error) {
                setSnackbarMessage('Failed to download file. Please try again.');
                setSnackbarOpen(true);
                return { url: '', error: 'Download failed' };
            }
        },
        [downloadFile],
    );

    const dataFileTableColumnConfig = useFileListColumnsConfig({
        onDelete: handleDeleteFile,
        onDownload: handleDownload,
    });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [uploadError, setUploadError] = useState<string | undefined>();
    const onCloseDialogClick = useCallback(() => {
        setIsDialogOpen(false);
    }, []);
    const onSubmit = useCallback(
        async (form: UploadFileFormValidValues) => {
            setUploadError(undefined);

            let file = form.file;
            if (form.fileName !== form.file.name) {
                file = new File([file], form.fileName, { type: file.type });
            }

            try {
                await uploadFile({ file });
                setIsDialogOpen(false);
                await refreshList().catch();
            } catch {
                setUploadError('An error occurred during file upload.');
            }
        },
        [refreshList],
    );
    const confirmDelete = useCallback(async () => {
        if (!selectedFileToDelete) return;
        try {
            const response = await deleteFile(selectedFileToDelete);
            if (response.status === 409) {
                setSnackbarMessage(`${response.message}. List the IDs of the table configurations: ${response.data}`);
            } else {
                setSnackbarMessage(` ${response.message} `);
            }
        } catch (error) {
            setSnackbarMessage('Failed to delete file. Please try again.');
        } finally {
            setSnackbarOpen(true);
            setConfirmDialogOpen(false);
        }
    }, [selectedFileToDelete, deleteFile]);

    return (
        <>
            <Dialog
                maxWidth="sm"
                TransitionComponent={PopupTransition}
                fullWidth
                open={isDialogOpen}
                sx={{ '& .MuiDialog-paper': { p: 0 } }}
            >
                <UploadFileDialog
                    initialValues={EMPTY_FILE_UPLOAD_FORM}
                    errorMessage={uploadError}
                    onCancel={onCloseDialogClick}
                    onSubmit={onSubmit}
                />
            </Dialog>
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Data File</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedFileToDelete && (
                            <p>
                                Do you want to delete this data file{' '}
                                <strong>
                                    {selectedFileToDelete.fileId}. {selectedFileToDelete.name}
                                </strong>
                                ?
                            </p>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        sx={{
                            color: 'rgb(255,100,100)',
                            border: 'none',
                            '&:hover': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                border: 'none',
                            },
                            '&:focus': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                outline: 'none',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="primary" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <MainCard content={false}>
                <Stack direction="column" component="div">
                    <Box sx={{ p: 2 }}>
                        <Tooltip
                            title={
                                !isManagementAllowed ? <FormattedMessage id="inside-fifa.no-permission-tip" /> : null
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setIsDialogOpen(true);
                                    }}
                                    disabled={!isManagementAllowed}
                                >
                                    <FormattedMessage id="inside-fifa.generic-tables.upload-file" />
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Stack>
                <BaseTable<GenericTableDataFileDto>
                    data={files}
                    fetchData={fetchFiles}
                    pageCount={pageCount}
                    defaultColumnConfig={dataFileTableColumnConfig}
                    defaultVisibleColumnConfig={DefaultVisibleDataFileTableColumnConfig}
                    onClick={() => {
                        if (selectedFileToDelete) {
                            deleteFile(selectedFileToDelete);
                        }
                    }}
                    onClickDownload={() => {
                        if (selectedFileToDownload) {
                            downloadFile(selectedFileToDownload);
                        }
                    }}
                />
            </MainCard>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </>
    );
};

export default GenericTablesDataPage;
